/**
 * Brand API client for scraped_data merchants.
 * List uses cursor pagination { results, page_size, next_cursor }.
 * Default sort=popular, page_size=48. Pass `cursor` (the previous
 * response's `nextCursor`) to fetch the following page.
 */

import { getApiBaseUrl } from "@/lib/auth/oauth";

class BrandApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

export const CIN_MATCH_STATUS_OPTIONS = [
  { value: "", label: "Any CIN match" },
  { value: "matched", label: "Matched" },
  { value: "review", label: "Review" },
  { value: "no_match", label: "No match" },
];

export const INCORPORATION_AGE_OPTIONS = [
  { value: "", label: "Any incorporation date" },
  { value: "lt1", label: "Under 1 year" },
  { value: "1_3", label: "1–3 years" },
  { value: "3_5", label: "3–5 years" },
  { value: "5plus", label: "5+ years" },
];

export function formatIncorporationDate(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    const text = String(value);
    return text.slice(0, 10);
  }
  return parsed.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function formatCinCompanyName(name) {
  const text = String(name || "").trim();
  if (!text) return "";
  if (text !== text.toUpperCase()) return text;
  return text.toLowerCase().replace(/\b([a-z])/g, (letter) => letter.toUpperCase());
}

export function cinMatchStatusLabel(status) {
  const key = String(status || "")
    .trim()
    .toLowerCase()
    .replace("-", "_");
  if (key === "matched") return "Matched";
  if (key === "review") return "Review";
  if (key === "no_match" || key === "nomatch") return "No match";
  return "";
}

function toStringList(value) {
  if (!Array.isArray(value)) return [];
  return value.map((v) => String(v ?? "").trim()).filter(Boolean);
}

function normalizeCinMatch(raw) {
  const source = raw?.cin_match || raw?.cinMatch;
  if (!source || typeof source !== "object") return null;
  const status = String(source.status || "")
    .trim()
    .toLowerCase()
    .replace("-", "_");
  return {
    status,
    cin: source.cin ? String(source.cin).toUpperCase() : "",
    companyName: source.company_name || source.companyName || "",
    dateOfIncorporation: source.date_of_incorporation || source.dateOfIncorporation || "",
    percentage: source.percentage ?? null,
    registeredAddress: source.registered_address || source.registeredAddress || "",
  };
}

export function normalizeBrand(raw) {
  if (!raw || typeof raw !== "object") return null;

  const tagline = raw.tagline || raw.description || raw.title || "";
  const companyName = raw.company_name || raw.companyName || [];
  const companyDisplayName =
    raw.company_display_name ||
    raw.companyDisplayName ||
    (Array.isArray(companyName)
      ? companyName.find((n) => /pvt|private|ltd|limited/i.test(String(n || ""))) ||
        companyName.find((n) => n && !/^[UL]\d{5}/i.test(String(n).replace(/\s/g, ""))) ||
        ""
      : "") ||
    "";
  const cinMatch = normalizeCinMatch(raw);
  const cin =
    raw.cin ||
    cinMatch?.cin ||
    (Array.isArray(companyName)
      ? (companyName.map((n) => String(n || "").match(/\b([UL]\d{5}[A-Z]{2}\d{4}[A-Z]{3}\d{6})\b/i)).find(Boolean) ||
          [])[1] || ""
      : "");

  // `logo_url` is a public HTTPS URL built by the backend from the stored logo path; null when the brand has no logo.
  const logoUrl = raw.logo_url || raw.logoUrl || "";

  const logo = logoUrl || raw.logo || raw.favicon_url || raw.faviconUrl || raw.icon || "";

  const coverImage = raw.cover_image || raw.coverImage || raw.banner_url || raw.bannerUrl || "";

  const openGraphImageUrl =
    raw.open_graph_image_url || raw.openGraphImageUrl || raw.og_image || raw.ogImage || coverImage || "";

  const image = raw.image || openGraphImageUrl || raw.logo_url || "";

  return {
    id: raw.id,
    name: raw.name || "",
    category: raw.category || "",
    categorySlug: raw.category || raw.categorySlug || "",
    website: raw.website || "",
    image,
    logo,
    logoUrl,
    coverImage,
    openGraphImageUrl,
    tagline,
    city: raw.city || "",
    state: raw.state || "",
    title: raw.title || "",
    emails: raw.emails || "",
    phones: raw.phones || "",
    whatsappUrl: raw.whatsapp_url || raw.whatsappUrl || "",
    facebookUrl: raw.facebook_url || raw.facebookUrl || "",
    instagramUrl: raw.instagram_url || raw.instagramUrl || "",
    linkedinUrl: raw.linkedin_url || raw.linkedinUrl || "",
    pinterestUrl: raw.pinterest_url || raw.pinterestUrl || "",
    youtubeUrl: raw.youtube_url || raw.youtubeUrl || "",
    aboutUsUrl: raw.about_us_url || raw.aboutUsUrl || "",
    contactPageUrl: raw.contact_page_url || raw.contactPageUrl || "",
    faqPageUrl: raw.faq_page_url || raw.faqPageUrl || "",
    returnsPageUrl: raw.returns_page_url || raw.returnsPageUrl || "",
    trackingPageUrl: raw.tracking_page_url || raw.trackingPageUrl || "",
    storeLocatorUrl: raw.store_locator_url || raw.storeLocatorUrl || "",
    brandsPageUrl: raw.brands_page_url || raw.brandsPageUrl || "",
    registeredAddress: raw.registered_address || raw.registeredAddress || "",
    returnAddress: raw.return_address || raw.returnAddress || "",
    storeAddress: raw.store_address || raw.storeAddress || "",
    returnPolicyAvailability: raw.return_policy_availability || raw.returnPolicyAvailability || "",
    returnPolicyLogistics: raw.return_policy_logistics || raw.returnPolicyLogistics || "",
    returnCondition: toStringList(raw.return_condition ?? raw.returnCondition),
    refundCondition: toStringList(raw.refund_condition ?? raw.refundCondition),
    codCondition: toStringList(raw.cod_condition ?? raw.codCondition),
    cancellationCondition: toStringList(raw.cancellation_condition ?? raw.cancellationCondition),
    cleaned: Boolean(raw.cleaned),
    cancellationPolicy:
      raw.cancellation_policy ||
      raw.cancellationPolicy ||
      toStringList(raw.cancellation_condition ?? raw.cancellationCondition).join(" "),
    shipsToCountries: raw.ships_to_countries || raw.shipsToCountries || "",
    codAvailable: raw.cod_available ?? raw.codAvailable ?? null,
    companyName,
    companyDisplayName: companyDisplayName || "",
    cin: cin ? String(cin).toUpperCase() : "",
    cinMatch,
    cinMatchStatus: cinMatch?.status || "",
    cinCompanyName: cinMatch?.companyName || "",
    dateOfIncorporation: cinMatch?.dateOfIncorporation || "",
    domainCreatedAt: raw.domain_created_at || raw.domainCreatedAt || "",
    domainAge: raw.domain_age || raw.domainAge || "",
    estimatedMonthlyVisits: raw.estimated_monthly_visits ?? raw.estimatedMonthlyVisits ?? 0,
    estimatedMonthlyRevenue: raw.estimated_monthly_revenue ?? raw.estimatedMonthlyRevenue ?? 0,
    lastScrapedAt: raw.last_scraped_at || raw.lastScrapedAt || "",
    verified: Boolean(raw.verified ?? raw.is_verified ?? raw.isVerified),
    verifiedSince: raw.verified_since || raw.verifiedSince || "",
    seo: raw.seo || null,
  };
}

// The edge (functions/brands/[slug].js) embeds the brand JSON it rendered into the
// HTML so the first paint needs no second API call. Valid for the page it was
// rendered for, and only for a few minutes.
const PRELOAD_MAX_AGE_MS = 5 * 60 * 1000;
const PRELOAD_LOADED_AT = Date.now();

function readPreload(elementId) {
  if (typeof document === "undefined" || Date.now() - PRELOAD_LOADED_AT > PRELOAD_MAX_AGE_MS) return null;
  try {
    const node = document.getElementById(elementId);
    return node ? JSON.parse(node.textContent || "null") : null;
  } catch (err) {
    return null;
  }
}

/** Sync read of the edge-rendered brand for `brandId`, normalized; null if absent or for another brand. */
export function peekPreloadedBrand(brandId) {
  const raw = readPreload("__RIVYOU_BRAND__");
  const wanted = String(brandId || "")
    .toLowerCase()
    .trim();
  if (!raw || !wanted || String(raw.id || "").toLowerCase() !== wanted) return null;
  return normalizeBrand(raw);
}

/** Edge-rendered directory/category page payload ({results,page,total_pages,...}), or null. */
export function peekPreloadedDirectory() {
  return readPreload("__RIVYOU_DIRECTORY__");
}

export function normalizeListPayload(data) {
  return {
    results: (data?.results || []).map(normalizeBrand).filter(Boolean),
    nextCursor: data?.next_cursor ?? null,
    pageSize: data?.page_size ?? 0,
    source: data?.source || "",
  };
}

async function fetchBrandJson(endpoint) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 6000);

  const url = endpoint.startsWith("http") ? endpoint : `${getApiBaseUrl()}${endpoint}`;

  try {
    const res = await fetch(url, {
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (!res.ok) {
      console.warn(`[Rivyou API] Request to ${url} returned status ${res.status}`);
      return {};
    }

    return await res.json();
  } catch (err) {
    clearTimeout(timeoutId);
    console.error(`[Rivyou API Error] Failed to fetch from backend (${url}):`, err);
    return {};
  }
}

/**
 * GET /api/brands/
 */
export async function getBrands(params = {}) {
  const query = new URLSearchParams();
  // `limit` is kept as an alias for `page_size`; the API paginates by cursor.
  const { limit, ...rest } = params;
  const merged = {
    page_size: limit ?? 48,
    sort: "popular",
    ...rest,
  };

  Object.entries(merged).forEach(([key, val]) => {
    if (val === undefined || val === null || val === "" || val === "all") return;
    if (typeof val === "boolean") {
      query.set(key, val ? "true" : "false");
      return;
    }
    query.set(key, String(val));
  });

  const queryString = query.toString() ? `?${query.toString()}` : "";

  const data = await fetchBrandJson(`/api/brands/${queryString}`);

  return normalizeListPayload(data);
}

/**
 * GET /api/brands/featured/
 */
export async function getFeaturedBrands(limit = 6) {
  const data = await fetchBrandJson(`/api/brands/featured/?limit=${limit}`);

  return normalizeListPayload(data).results;
}

/**
 * GET /api/brands/categories/
 */
export async function getBrandCategories() {
  const data = await fetchBrandJson(`/api/brands/categories/`);

  if (!Array.isArray(data)) return [];
  return data.map((cat) => ({
    id: cat.id,
    label: cat.label || cat.id,
    slug: cat.slug || "",
    count: cat.count,
  }));
}

/**
 * GET /api/brands/seo/directory/ — crawlable, offset-paged list of indexable brands.
 * Returns null when the page/category doesn't exist.
 */
export async function getSeoDirectory({ page = 1, category = "" } = {}) {
  const preloaded = peekPreloadedDirectory();
  if (preloaded && preloaded.page === page && (preloaded.category_slug || "") === (category || "")) {
    return preloaded;
  }
  const query = new URLSearchParams({ page: String(page) });
  if (category) query.set("category", category);
  try {
    const res = await fetch(`${getApiBaseUrl()}/api/brands/seo/directory/?${query}`, {
      headers: { Accept: "application/json" },
    });
    return res.ok ? await res.json() : null;
  } catch (err) {
    return null;
  }
}

/**
 * GET /api/brands/:brandId/
 */
export async function getBrandById(brandId) {
  const normalizedId = String(brandId || "")
    .toLowerCase()
    .trim();
  if (!normalizedId) return null;

  const preloaded = peekPreloadedBrand(normalizedId);
  if (preloaded) return preloaded;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 6000);
  const url = `${getApiBaseUrl()}/api/brands/${encodeURIComponent(brandId)}/`;

  try {
    const res = await fetch(url, {
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (res.ok) {
      const data = await res.json();
      if (data && data.name) {
        return normalizeBrand(data);
      }
    } else {
      console.warn(`[Rivyou API] Brand ${brandId} fetch returned status ${res.status}`);
    }
  } catch (err) {
    clearTimeout(timeoutId);
    console.error(`[Rivyou API Error] Failed to fetch brand ${brandId} from (${url}):`, err);
  }

  return null;
}

/**
 * POST /api/brands/request-audit/
 */
export async function requestBrandAudit(payload) {
  const timestamp = new Date().toISOString();
  const requestRecord = {
    id: `req_${Date.now()}`,
    ...payload,
    created_at: timestamp,
    status: "pending_review",
  };

  try {
    const url = `${getApiBaseUrl()}/api/brands/request-audit/`;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(requestRecord),
    });

    if (res.ok) {
      const data = await res.json().catch(() => ({}));
      return { success: true, ...data };
    }
  } catch (err) {
    // Graceful offline fallback
  }

  try {
    const existing = JSON.parse(localStorage.getItem("rivyou_brand_audit_requests") || "[]");
    existing.unshift(requestRecord);
    localStorage.setItem("rivyou_brand_audit_requests", JSON.stringify(existing.slice(0, 50)));
  } catch (e) {
    // Ignore storage issues
  }

  return {
    success: true,
    offline: true,
    message: "Brand audit request queued successfully for review.",
  };
}

const GOOGLE_SHEETS_CORRECTIONS_URL =
  process.env.REACT_APP_GOOGLE_SHEETS_CORRECTIONS_URL ||
  "https://script.google.com/macros/s/AKfycbwZuV9OxRhj9mM-5jPUOFvz3ggGR_snYsA5wRrHYgyI2dD4YdbD0XKLthlDgfT06y6yJg/exec";

export async function submitBrandCorrection(payload) {
  const timestamp = new Date().toISOString();
  const requestRecord = {
    id: `corr_${Date.now()}`,
    ...payload,
    created_at: timestamp,
    status: "pending_review",
  };

  const sheetUrl = process.env.REACT_APP_GOOGLE_SHEETS_CORRECTIONS_URL || GOOGLE_SHEETS_CORRECTIONS_URL;

  try {
    await fetch(sheetUrl, {
      method: "POST",
      mode: "no-cors",
      headers: {
        "Content-Type": "text/plain",
      },
      body: JSON.stringify(requestRecord),
    });
    return { success: true };
  } catch (err) {
    // Fallback to LocalStorage if the sheet is unreachable
    try {
      const existing = JSON.parse(localStorage.getItem("rivyou_brand_corrections") || "[]");
      existing.unshift(requestRecord);
      localStorage.setItem("rivyou_brand_corrections", JSON.stringify(existing.slice(0, 50)));
    } catch (e) {
      // Ignore storage issues
    }

    return {
      success: true,
      offline: true,
      message: "Correction queued for review.",
    };
  }
}

export { BrandApiError };
